import { CcfBox, useTranslator } from '@nice-ccf/ui-controls';
import styles from './ccf-consumer-card-tickets-tab.module.scss';
import CcfConsumerCardTicketsTabHeader from './ccf-consumer-card-tickets-tab-header';
import {
  isToday,
  isYesterday,
  parseISO,
  subDays,
  isWithinInterval,
} from 'date-fns';
import { useEffect, useState } from 'react';
import CcfConsumerCardTicketsTabTable from './ccf-consumer-card-tickets-tab-table';

/* eslint-disable-next-line */
export interface CcfConsumerCardTicketsTabProps {}

/**@example */
export function CcfConsumerCardTicketsTab(
  props: CcfConsumerCardTicketsTabProps
) {
  const [translate] = useTranslator();

  const ticketTabDetails = [
    {
      id: 1,
      priority: translate('high'),
      status: translate('open'),
      agent: 'Agent Smith',
      date: new Date(new Date(Date.now()).setHours(8, 1, 0, 0)).toISOString(),
      subject: translate('onboarding_request_service'),
    },
    {
      id: 2,
      priority: translate('low'),
      status: translate('open'),
      agent: 'Agent Kapoor',
      date: new Date(new Date(Date.now()).setHours(10, 30, 0, 0)).toISOString(),
      subject: translate('onboarding_request_service'),
    },
    {
      id: 3,
      priority: translate('medium'),
      status: translate('open'),
      agent: 'Agent Tiwari',
      date: new Date(
        new Date(Date.now() - 86400000).setHours(14, 10, 0, 0)
      ).toISOString(),
      subject: translate('airline_customer_service'),
    },
    {
      id: 4,
      priority: translate('high'),
      status: translate('closed'),
      agent: 'Agent Smith',
      date: new Date(
        new Date(Date.now() - 86400000).setHours(9, 30, 0, 0)
      ).toISOString(),
      subject: translate('airline_customer_service'),
    },
    {
      id: 5,
      priority: translate('medium'),
      status: translate('closed'),
      agent: 'Agent Franciso',
      date: new Date(
        new Date(Date.now() - 86400000).setHours(2, 30, 0, 0)
      ).toISOString(),
      subject: translate('onboarding_request_service'),
    },
    {
      id: 6,
      priority: translate('low'),
      status: translate('closed'),
      agent: 'Service Bot',
      date: new Date(
        new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).setHours(2, 30, 0, 0)
      ).toISOString(),
      subject: translate('onboarding_request_service'),
    },
    {
      id: 7,
      priority: translate('low'),
      status: translate('closed'),
      agent: 'Agent Kapoor',
      date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      subject: translate('onboarding_request_service'),
    },
    {
      id: 8,
      priority: translate('high'),
      status: translate('open'),
      agent: 'Agent Tiwari',
      date: new Date(
        new Date().setMonth(new Date().getMonth() - 1)
      ).toISOString(),
      subject: translate('onboarding_request_service'),
    },
    {
      id: 9,
      priority: translate('low'),
      status: translate('closed'),
      agent: 'Agent Smith',
      date: new Date(
        new Date().setMonth(new Date().getMonth() - 1)
      ).toISOString(),
      subject: translate('onboarding_request_service'),
    },
    {
      id: 10,
      priority: translate('medium'),
      status: translate('closed'),
      agent: 'Service Bot',
      date: new Date(
        new Date().setMonth(new Date().getMonth() - 3)
      ).toISOString(),
      subject: translate('onboarding_request_service'),
    },
    {
      id: 11,
      priority: translate('high'),
      status: translate('open'),
      agent: 'Service Bot',
      date: new Date(
        new Date().setMonth(new Date().getMonth() - 3)
      ).toISOString(),
      subject: translate('onboarding_request_service'),
    },
    {
      id: 12,
      priority: translate('high'),
      status: translate('closed'),
      agent: 'Agent Smith',
      date: new Date(
        new Date().setMonth(new Date().getMonth() - 3)
      ).toISOString(),
      subject: translate('onboarding_request_service'),
    },
  ];

  const [selectedFilterOption, setSelectedFilterOption] = useState('All');
  // Mock data
  const [ticketsData, setTicketsData] = useState(ticketTabDetails);

  /**@example */
  const filterData = (data: any, filterOption: any) => {
    const now = new Date();
    const filteredData = data.filter((item: any) => {
      const itemDate = parseISO(item.date);
      switch (filterOption) {
        case 'today':
          return isToday(itemDate);
        case 'yesterday':
          return isYesterday(itemDate);
        case 'lastWeek':
          return isWithinInterval(itemDate, {
            start: subDays(now, 7),
            end: now,
          });
        case 'lastMonth':
          return isWithinInterval(itemDate, {
            start: subDays(now, 30),
            end: now,
          });
        case 'last90Days':
          return isWithinInterval(itemDate, {
            start: subDays(now, 90),
            end: now,
          });
        default: // 'All'
          return true;
      }
    });

    return filteredData;
  };

  /**
   * Function to select interaction view
   * @example interactionViewSelectHandler()
   */
  const searchHandler = (searchQuery: string) => {
    if (searchQuery === '') {
      setTicketsData(ticketTabDetails);
    } else {
      const filteredData = ticketsData.filter((row: any) =>
        Object.values(row).some((value) =>
          String(value).toLowerCase().includes(searchQuery)
        )
      );

      setTicketsData(filteredData);
    }
  };

  /**@example */
  const handleFilter = (option: any) => {
    setSelectedFilterOption(option);
    const updatedData = filterData(ticketTabDetails, option);
    setTicketsData(updatedData);
  };
  return (
    <CcfBox style={{ height: '100%' }}>
      <CcfConsumerCardTicketsTabHeader
        searchHandler={searchHandler}
        handleFilter={handleFilter}
        selectedFilterOption={selectedFilterOption}
      />{' '}
      <CcfBox className={styles.ticketsContainer}>
        <CcfConsumerCardTicketsTabTable ticketsData={ticketsData} />
      </CcfBox>
    </CcfBox>
  );
}

export default CcfConsumerCardTicketsTab;
